import { Component, HostBinding, OnInit, QueryList, ViewChildren } from '@angular/core';
import { SelectionChangedEvent, RowNode } from 'ag-grid-community';
import * as _ from 'underscore';
import { AliasList } from '../../../../models/alias-lists';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, EntryType, HelpIndex, UpdateStatusType } from '../../../../models/enums';
import { CellTemplateType, GenericColDef, GridRowSelectionType, FilterTemplateType, GridContext } from '../../../../models/grid-context';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { AddAliasesDomainEntriesModalComponent } from '../../../modals/add-aliases-domain-entries-modal/add-aliases-domain-entries-modal.component';
import { CreateEditEntityModalComponent } from '../../create-edit-entity-modal/create-edit-entity-modal.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { Permissions, PermissionSet, ObjectActionListType, ActionType } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-alias-lists',
    templateUrl: './alias-lists.component.html',
    styleUrls: ['./alias-lists.component.scss']
})

export class AliasListsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;
    isDirty: boolean;
    selectedAliasList: AliasList;
    numberOfSelectedAliases: number;
    numberOfAliasLists: number;
    filterQuery = '';
    aliases = [];
    updateStatusType = UpdateStatusType.ALIAS_LISTS;

    gridName = 'Alias Lists Grid';
    automationId = 'alias-lists';
    aliasesAutomationId = 'alias-entries';
    helpIndex: any;
    isBusy = false;
    isBusyMessage = 'Loading Alias Lists ...';
    currentHandle = '';
    validChanges = true;

    // permissions
    permissionType: ObjectActionListType;
    hasALCreatePermissions: boolean;

    gridContext = new GridContext({
        inlineGridFilter: true,
        onFilterApply: (queryStr: string) => this.onFilterApply(queryStr),
    });

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Alias List Name',
            field: 'name',
            automationId: this.automationId,
            lockVisible: true,
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );

    colDefsAliases = new Array<GenericColDef>(
        {
            colId: 'checkbox',
            automationId: this.automationId,
            minWidth: 36, maxWidth: 36, width: 36,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            suppressSizeToFit: true,
        },
        {
            headerName: 'Alias Name',
            field: 'aliasName',
            automationId: this.automationId,
            filterType: FilterTemplateType.freeform,
            suppressMovable: true
        }
    );

    aliasListSelection = GridRowSelectionType.single;

    aliasesSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.projectManager_listAliasList,
        responseName: 'aliasList'
    });

    rowClassRules = {
        'disabled-row': (params) => {
            return !this.hasALCreatePermissions;
        }
    };

    constructor(
        private commonService: CommonService,
        private restService: RestService
    ) {
    }

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            click: (item) => this.editAliasList(item)
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            click: (item) => this.deleteAliasList(item)
        })
    ];

    editAliasList(item: AliasList): void {
        if (this.hasALCreatePermissions) {
            const aliasList = new AliasList(item);
            this.commonService.openModal(CreateEditEntityModalComponent, { modalData: aliasList }).then((response) => {
                if (response && response.modalData && response.modalData.aliasList) {
                    this.selectedAliasList = response.modalData.aliasList;
                }
            });
        }
    }

    deleteAliasList(item: AliasList): void {
        const aliasList = new AliasList(item);
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: aliasList }).then((response) => {
            this.selectedAliasList = null;
        });
    }

    addNewAliasList() {
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: new AliasList({ parentHandle: this.currentHandle }) }).then((response) => {
            if (response && response.modalData && response.modalData.aliasList) {
                this.selectedAliasList = new AliasList(response.modalData.aliasList);
            }
        });
    }

    addAliases(): void {
        this.commonService.openModal(AddAliasesDomainEntriesModalComponent, {
            backdrop: false,
            modalData: {
                entryType: EntryType.aliases,
                listSelected: this.selectedAliasList
            },
            class: 'horizontal-px-500'
        }).then(response => {
            if (response && response.modalData && response.modalData.aliasList && response.modalData.aliasList.aliases) {
                const allAliases = response.modalData.aliasList.aliases;
                const al = [];
                allAliases.forEach((alias, index) => {
                    if (alias !== '') {
                        return al[index] = {
                            aliasName: alias
                        };
                    }
                });
                this.selectedAliasList.aliases = allAliases;
                this.aliases = al;
                if (this.filterQuery !== '') {
                    this.onFilterApply(this.filterQuery);
                } else {
                    this.genericGrids.last.setData(al);
                }
            }
        });
    }

    responseCallback = (response: any) => {
        this.selectedAliasList = this.selectedAliasList ? this.selectedAliasList : new AliasList(response.aliasList[0]);
        this.numberOfAliasLists = response.aliasList.length;

        if (response.aliasList.length === 0) {
            this.genericGrids.last.setData([]);
        }
    }

    loadDataFinishedCallback = () => {
        if (this.genericGrids.first.gridApi) {
            if (this.selectedAliasList && this.selectedAliasList.handle) {
                this.genericGrids.first.gridApi.forEachNode((node) => {
                    if (this.selectedAliasList.handle === node.data.handle) {
                        node.setSelected(true);
                        this.genericGrids.first.gridApi.ensureNodeVisible(node);
                    }
                });
            } else {
                const aliasRows = this.genericGrids.first.getRows();
                if (aliasRows && aliasRows.length > 0) {
                    this.selectedAliasList = aliasRows[0].data;
                    this.genericGrids.first.selectNode(aliasRows[0]);
                } else {
                    this.setSelectedAliasList();
                }
            }
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api && this.genericGrids.first.gridApi) {
            this.selectedAliasList = event.api.getSelectedRows()[0];
            if (this.selectedAliasList && this.selectedAliasList.aliases && this.selectedAliasList.aliases.length > 0) {
                this.setSelectedAliasList();
            } else {
                this.genericGrids.last.setData([]);
            }
        }
    }

    onCustomFilterCallback = (event?: SelectionChangedEvent) => {
        this.setSelectedAliasList();
    }

    setSelectedAliasList(): void {
        const al = [];
        if (this.selectedAliasList && this.selectedAliasList.aliases) {
            this.selectedAliasList.aliases.forEach((alias, index) => {
                return al[index] = {
                    aliasName: alias
                };
            });
        }
        this.aliases = al;
        if (this.filterQuery !== '') {
            this.onFilterApply(this.filterQuery);
        } else {
            this.genericGrids.last.setData(al);
        }
    }

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data) {
            const selAliasList = new AliasList(rowNode.data);
            if (selAliasList) {
                this.editAliasList(selAliasList);
            }
        }
    }

    countSelectedAliases = (event?: SelectionChangedEvent) => {
        if (event && event.api && this.genericGrids.last.gridApi) {
            this.numberOfSelectedAliases = event.api.getSelectedRows().length;
        }
    }

    deleteSelectedAliases() {
        if (this.genericGrids?.last?.gridApi) {
            const selectedAliasesForDeletion = this.genericGrids.last.gridApi.getSelectedRows(),
                aliasesAvailable = [];
            if (selectedAliasesForDeletion.length === 0) {
                return;
            }
            let aliases = [];
            selectedAliasesForDeletion.forEach((alias, index) => {
                aliasesAvailable[index] = alias.aliasName;
            });
            aliases = _.difference(this.selectedAliasList.aliases, aliasesAvailable);

            this.restService.post(Endpoint.projectManager_updateAliasList, {
                handle: this.selectedAliasList.handle,
                description: this.selectedAliasList.description,
                name: this.selectedAliasList.name,
                aliases: aliases
            }).then(response => {
                this.listAliases();
            });
        }
    }

    listAliases(item?: any): void {
        this.restService.post(Endpoint.projectManager_listAliasList, {
            handle: this.currentHandle,
            count: 50,
            startIndex: 0
        }).then(response => {
            if (response.aliasList && response.aliasList.length > 0) {
                this.aliases = [];
                const currentAliasList = _.findWhere(response.aliasList, { handle: this.selectedAliasList.handle });
                if (currentAliasList && currentAliasList.aliases && currentAliasList.aliases.length > 0) {
                    currentAliasList.aliases.forEach((alias, index) => {
                        return this.aliases[index] = {
                            aliasName: alias
                        };
                    });
                }
                this.genericGrids.first.setData(response.aliasList);
                if (this.filterQuery !== '') {
                    this.onFilterApply(this.filterQuery);
                } else {
                    this.genericGrids.last.setData(this.aliases);
                }

                this.selectedAliasList = currentAliasList;
            } else {
                this.genericGrids.first.setData([]);
                this.genericGrids.last.setData([]);
            }
        });
    }

    onFilterApply(queryStr: string): void {
        this.filterQuery = queryStr;
        const filtered = _.filter(this.aliases, (field) => field.aliasName ? field.aliasName.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1 : false);
        this.genericGrids.last.setData(filtered);
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string): void {
        this.currentHandle = node.data instanceof Template ? node.data.handle : projectHandle;
        this.helpIndex = projectHandle ? HelpIndex.myedisc_case_alias_lists_dlg : HelpIndex.org_alias_lists_template_dlg;
        this.permissionType = projectHandle ? ObjectActionListType.PRJ_ALIAS_LISTS : ObjectActionListType.ORG_ALIAS_LISTS;

        this.hasALCreatePermissions = Permissions.checkUserPermission(this.permissionType, ActionType.CREATE);

        if (projectHandle) {
            this.contextMenuOptions[1].permissions = [new PermissionSet({ permissionType: this.permissionType, permissionActions: [ActionType.DELETE] })];
        }
        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (!this.hasALCreatePermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
            }
        }

        if (this.colDefsAliases.find(c => c.colId === 'checkbox')) {
            if (!this.hasALCreatePermissions) {
                this.colDefsAliases = this.colDefsAliases.filter(col => col.colId !== 'checkbox');
            }
        }

        if (!this.hasALCreatePermissions) {
            this.contextMenuOptions = null;
        }

        if (this.currentHandle) {
            this.requestInfo.data = {
                handle: this.currentHandle
            };

            if (this.genericGrids && this.genericGrids.first && this.genericGrids.last) {
                this.selectedAliasList = this.selectedAliasList ? this.selectedAliasList : null;
                this.genericGrids.first.setData([]);
                this.genericGrids.last.setData([]);
                this.genericGrids.first.loadData();
                this.genericGrids.last.isDataFiltered = false;
                this.genericGrids.last.gridContext.clearAllFilters.emit();
            }
        }
    }
}
